// app/components/FundraisingCards.tsx
'use client';

import Image from 'next/image';

interface FundraisingCard {
  id: string;
  title: string;
  description: string;
  image: string;
  coverImage: string;
  targetAmount: number;
  collectedAmount: number;
  donorsCount: number;
  daysLeft: number;
  urgencyLevel: 'high' | 'medium' | 'low';
  category: string;
}

const FundraisingCards = () => {
  const cards: FundraisingCard[] = [
    {
      id: '1',
      title: 'Bantuan Operasi Pak Mulyadi',
      description: 'Seorang ayah yang membutuhkan bantuan biaya operasi segera untuk kesembuhannya',
      image: '👨‍⚕️',
      coverImage: 'https://images.pexels.com/photos/33217722/pexels-photo-33217722.jpeg',
      targetAmount: 50000000,
      collectedAmount: 23500000,
      donorsCount: 1247,
      daysLeft: 14,
      urgencyLevel: 'high',
      category: 'Kesehatan'
    },
    {
      id: '2',
      title: 'Operasi Jantung Anak Syahla',
      description: 'Anak kecil yang membutuhkan operasi jantung untuk bertahan hidup',
      image: '👧',
      coverImage: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      targetAmount: 75000000,
      collectedAmount: 45200000,
      donorsCount: 2156,
      daysLeft: 76,
      urgencyLevel: 'medium',
      category: 'Kesehatan'
    },
    {
      id: '3',
      title: 'Renovasi Sekolah Yatim',
      description: 'Membangun masa depan anak-anak yatim dengan fasilitas pendidikan yang layak',
      image: '🏫',
      coverImage: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      targetAmount: 100000000,
      collectedAmount: 67800000,
      donorsCount: 892,
      daysLeft: 30,
      urgencyLevel: 'low',
      category: 'Pendidikan'
    }
  ];

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate progress percentage
  const calculateProgress = (collected: number, target: number) => {
    return Math.min((collected / target) * 100, 100);
  };

  return (
    <div className="space-y-5">
      {/* Section Header */}
      <div className="flex items-center justify-between px-2">
        <div>
          <h3 className="font-bold text-lg text-gray-900 px-6">
            Donasi Mendesak
          </h3>
          <p className="text-gray-600 text-sm px-6">
            Program yang membutuhkan bantuan segera
          </p>
        </div>
        <button className="flex items-center space-x-1 text-orange-700 font-semibold text-[12px] hover:text-gray-900 transition-colors">
          <span>Lihat Semua</span>
        </button>
      </div>

      {/* Scrollable Cards */}
      <div className="overflow-x-auto pb-2 px-6">
        <div className="flex space-x-4 px-2">
          {cards.map((card) => {
            const progress = calculateProgress(card.collectedAmount, card.targetAmount);
            
            return (
              <div
                key={card.id}
                className="flex-shrink-0 w-72 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden group hover:shadow-md transition-all duration-300"
              >
                {/* Image Cover */}
                <div className="w-full h-50 overflow-hidden">
                  <Image 
                    src={card.coverImage || '/api/placeholder/288/160'} 
                    width={288}
                    height={160}
                    alt={card.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Card Header */}
                <div className="p-3 pb-1"> 
                  {/* Content */}
                  <h4 className="font-bold text-gray-900 text-base mb-1 line-clamp-2 group-hover:text-gray-700 transition-colors">
                    {card.title}
                  </h4>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2 leading-relaxed">
                    {card.description}
                  </p>
                </div>

                {/* Progress Section */}
                <div className="px-3 pb-5">
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-500 font-medium">Progress</span>
                      <span className="text-xs font-bold text-gray-700">
                        {progress.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gray-700 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Amount Info */}
                  <div className="space-y-2 mb-1">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Terkumpul</span>
                      <span className="font-bold text-gray-900 text-sm">
                        {formatCurrency(card.collectedAmount)}
                      </span>
                    </div>
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FundraisingCards;